/**
 * Frame-rate cap arithmetic.
 *
 * Split out of 'Game' so it can be tested without a browser: the failure mode
 * of a cap is an off-by-one against the vsync boundary, which silently halves
 * the frame rate rather than throwing anything, and that is not something to
 * discover by feel. See tools/frame-cap.test.ts.
 */

/**
 * The cap in force, given which screen is up.
 *
 * The menu cap only ever lowers the rate: if a player has explicitly asked for
 * 30 fps, being in a menu must not push them back up to 60.
 */
export function activeFpsCap(inMenu: boolean, fpsCap: number, menuFpsCap: number): number {
  if (!inMenu || menuFpsCap <= 0) return fpsCap;
  return fpsCap > 0 ? Math.min(menuFpsCap, fpsCap) : menuFpsCap;
}

/**
 * Whether a frame should be presented now.
 *
 * requestAnimationFrame only fires on vsync boundaries, so a naive
 * `now - last >= 1000 / cap` misses the boundary it is aiming at whenever the
 * budget lands exactly on one — a 60 fps cap on a 60 Hz panel would present
 * every *second* frame and deliver 30. Treating anything within half a refresh
 * interval of the target as due picks the nearest boundary instead, which is
 * both correct at exact divisors and stable when the cap is not a divisor.
 */
export function frameDue(
  now: number, lastPresent: number, capFps: number, refreshHz: number,
): boolean {
  if (capFps <= 0) return true;
  const tolerance = (1000 / Math.max(1, refreshHz)) * 0.5;
  return now - lastPresent >= 1000 / capFps - tolerance;
}
